import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import InfoDrawer from '@/components/InfoDrawer';
import FAQPanel from '@/components/FAQPanel';
import { helpTopics, type HelpTopicId } from './topics';
import { faqTopics, type FaqTopicId } from './faqTopics';

interface HelpContextValue {
  isOpen: boolean;
  topicId: HelpTopicId | null;
  open: (topicId: HelpTopicId) => void;
  openForSchema: (schema: { helpTopicId?: HelpTopicId | string; formName?: string }) => void;
  close: () => void;
  // FAQ specific controls
  openFaq: (topicId: FaqTopicId) => void;
  openFaqForSchema: (schema: { helpTopicId?: string; formName?: string }) => void;
}

// Default no-op implementations for fallback
const noop = () => {};
const noopWithArg = () => {};

const defaultContextValue: HelpContextValue = {
  isOpen: false,
  topicId: null,
  open: noop,
  openForSchema: noopWithArg,
  close: noop,
  openFaq: noop,
  openFaqForSchema: noopWithArg,
};

const HelpContext = createContext<HelpContextValue>(defaultContextValue);

export function HelpProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [topicId, setTopicId] = useState<HelpTopicId | null>(null);
  const [faqOpen, setFaqOpen] = useState(false);
  const [faqTopicId, setFaqTopicId] = useState<FaqTopicId | null>(null);

  const open = useCallback((id: HelpTopicId) => {
    setTopicId(id);
    setIsOpen(true);
  }, []);

  const openForSchema = useCallback((schema: { helpTopicId?: HelpTopicId | string; formName?: string }) => {
    // Prefer explicit schema.helpTopicId. Fallback to a generic onboarding help topic if available.
    const explicitId = schema.helpTopicId as HelpTopicId | undefined;
    const fallbackId: HelpTopicId = 'onboarding.general';
    const idToOpen: HelpTopicId = (explicitId && explicitId in helpTopics ? explicitId : fallbackId) as HelpTopicId;
    setTopicId(idToOpen);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const openFaq = useCallback((id: FaqTopicId) => {
    setFaqTopicId(id);
    setFaqOpen(true);
  }, []);

  const openFaqForSchema = useCallback((schema: { helpTopicId?: string; formName?: string }) => {
    // Map schema.helpTopicId (if present) to an FAQ topic; default to onboarding.general
    const explicit = schema.helpTopicId as FaqTopicId | undefined;
    const fallback: FaqTopicId = 'onboarding.general';
    const id: FaqTopicId = (explicit && explicit in faqTopics ? explicit : fallback) as FaqTopicId;
    setFaqTopicId(id);
    setFaqOpen(true);
  }, []);

  const closeFaq = useCallback(() => setFaqOpen(false), []);

  const value: HelpContextValue = useMemo(() => ({ isOpen, topicId, open, openForSchema, close, openFaq, openFaqForSchema }), [isOpen, topicId, open, openForSchema, close, openFaq, openFaqForSchema]);

  const content = topicId ? helpTopics[topicId] : undefined;
  const faqContent = faqTopicId ? faqTopics[faqTopicId] : undefined;

  return (
    <HelpContext.Provider value={value}>
      {children}
      {content && (
        <InfoDrawer open={isOpen} onClose={close} content={content} />
      )}
      {faqContent && (
        <FAQPanel open={faqOpen} onClose={closeFaq} content={faqContent} />
      )}
    </HelpContext.Provider>
  );
}

export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  // Context now has a default value, so we can safely return it
  // During hot reload or if provider is missing, default no-ops will be used
  return ctx;
}


